import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";

interface FeatureGuardProps {
  featureId: string;
  featureName?: string;
  children: React.ReactNode;
}

export default function FeatureGuard({
  featureId,
  featureName = "Feature",
  children,
}: FeatureGuardProps) {
  const { canAccess, role } = usePermissions();
  const navigate = useNavigate();

  if (canAccess(featureId)) {
    return <>{children}</>;
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "calc(100vh - 120px)",
        padding: "24px",
      }}
    >
      <div
        style={{
          background: "var(--color-surface, #12141a)",
          border: "1px solid var(--color-border, #1f2430)",
          borderRadius: "16px",
          padding: "40px 32px",
          maxWidth: "480px",
          width: "100%",
          textAlign: "center",
          boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
        }}
      >
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            background: "rgba(245, 158, 11, 0.12)",
            border: "1px solid rgba(245, 158, 11, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
            color: "#f59e0b",
          }}
        >
          <Lock size={30} />
        </div>

        <h2
          style={{
            fontSize: "1.35rem",
            fontWeight: 700,
            color: "var(--color-text, #ffffff)",
            marginBottom: "8px",
          }}
        >
          Access Restricted
        </h2>

        <p
          style={{
            fontSize: "0.88rem",
            color: "var(--color-text-muted, #94a3b8)",
            lineHeight: 1.55,
            marginBottom: "24px",
          }}
        >
          Your account role (<strong style={{ color: "#f59e0b", textTransform: "capitalize" }}>{role}</strong>)
          has not been granted access to <strong>{featureName}</strong>. Contact your system
          administrator if you require access to this module.
        </p>

        <button
          onClick={() => navigate("/dashboard")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: "var(--color-primary, #0066e0)",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            padding: "10px 20px",
            fontSize: "0.88rem",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <ArrowLeft size={16} /> Return to Dashboard
        </button>
      </div>
    </div>
  );
}
